import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import './InboxPage.css';

// ── 工具 ──
function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return '刚刚';
  if (m < 60) return `${m}分钟前`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}小时前`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}天前`;
  return d.toLocaleDateString();
}

function fullTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function isHealthy(status) {
  // status 仅 'OK' 视为健康；明显异常（CREDENTIALS 等）标警告；UNKNOWN 中性可用
  return !status || status === 'OK' || status === 'UNKNOWN';
}

function convDisplayName(c) {
  return c.contact_name || c.attendee_name || c.name
    || (c.attendee_provider_id ? `LinkedIn 用户 ${c.attendee_provider_id.slice(-6)}` : '未知联系人');
}

// 对方领英主页 URL：优先 campaign 带的真实 profile_url / public_identifier，
// 兜底用 attendee_provider_id（领英 /in/{ACoAA...} 可直接解析）
function profileUrl(c) {
  if (!c) return null;
  if (c.contact_profile_url) return c.contact_profile_url;
  if (c.contact_public_id) return `https://www.linkedin.com/in/${c.contact_public_id}`;
  if (c.attendee_provider_id) return `https://www.linkedin.com/in/${c.attendee_provider_id}`;
  return null;
}

// ── 顶部账号子 tab ──
function AccountTabs({ accounts, selectedId, unreadByAccount, onSelect }) {
  return (
    <div className="inbox-tabs">
      {accounts.map((a) => {
        const unread = unreadByAccount[a.account_id] || 0;
        const healthy = isHealthy(a.status);
        return (
          <button
            key={a.account_id}
            className={`inbox-tab ${a.account_id === selectedId ? 'active' : ''}`}
            onClick={() => onSelect(a.account_id)}
            title={healthy ? a.name : `${a.name}（账号状态: ${a.status}，只读）`}
          >
            {!healthy && <span className="inbox-tab-warn">⚠️</span>}
            <span className="inbox-tab-name">{a.name || a.account_id}</span>
            {unread > 0 && <span className="inbox-tab-badge">{unread}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ── 左侧会话列表 ──
function ConversationList({
  conversations, selectedConvId, loading, filter, q,
  onSelect, onFilterChange, onSearchChange, onLoadMore, hasMore,
}) {
  return (
    <div className="inbox-list">
      <div className="inbox-list-header">
        <div className="inbox-filter">
          <button className={filter === 'all' ? 'active' : ''} onClick={() => onFilterChange('all')}>全部</button>
          <button className={filter === 'unread' ? 'active' : ''} onClick={() => onFilterChange('unread')}>未读</button>
        </div>
        <input
          className="inbox-search"
          placeholder="搜索对话…"
          value={q}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="inbox-list-body">
        {conversations.length === 0 && !loading && (
          <div className="inbox-empty">没有对话</div>
        )}
        {conversations.map((c) => (
          <div
            key={c.id}
            className={`inbox-conv ${c.id === selectedConvId ? 'active' : ''} ${c.unread_count > 0 ? 'unread' : ''}`}
            onClick={() => onSelect(c)}
          >
            <div className="inbox-conv-top">
              <span className="inbox-conv-name">{convDisplayName(c)}</span>
              {c.unread_count > 0 && <span className="inbox-conv-dot" />}
              <span className="inbox-conv-time">{relTime(c.last_message_at)}</span>
            </div>
            <div className="inbox-conv-preview">
              {c.last_message_is_sender ? <span className="inbox-conv-me">我: </span> : null}
              <span className="inbox-conv-preview-text">
                {c.last_message_preview || <span className="inbox-conv-dim">（无文本内容）</span>}
              </span>
            </div>
          </div>
        ))}
        {hasMore && (
          <button className="inbox-loadmore" onClick={onLoadMore} disabled={loading}>
            {loading ? '加载中…' : '加载更多'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── 右侧消息流 + 回复框 ──
function MessageThread({ conv, messages, loading, canReply, replyDisabledReason, readSyncError, onSend, sending, onView }) {
  const [text, setText] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'auto' });
  }, [messages]);

  if (!conv) {
    return <div className="inbox-thread inbox-thread-empty">选择左侧一个对话开始查看</div>;
  }

  const handleSend = () => {
    const t = text.trim();
    if (!t || sending) return;
    onSend(t);
    setText('');
  };

  return (
    <div className="inbox-thread">
      <div className="inbox-thread-header">
        <div className="inbox-thread-name">
          {profileUrl(conv) ? (
            <a href={profileUrl(conv)} target="_blank" rel="noopener noreferrer" className="inbox-thread-link"
               title="在领英打开主页">
              {convDisplayName(conv)} ↗
            </a>
          ) : convDisplayName(conv)}
        </div>
        {conv.contact_headline && <div className="inbox-thread-headline">{conv.contact_headline}</div>}
        {conv.campaign_id && (
          <a className="inbox-thread-campaign" href={`/campaigns/${conv.campaign_id}/status`}>Campaign #{conv.campaign_id}</a>
        )}
      </div>

      <div className="inbox-thread-body" onClick={onView} onScroll={onView}>
        {loading && <div className="inbox-empty">加载消息中…</div>}
        {!loading && messages.map((m) => (
          <div key={m.id} className={`inbox-msg ${m.is_sender ? 'out' : 'in'}`}>
            <div className="inbox-msg-bubble">
              {m.subject && <div className="inbox-msg-subject">{m.subject}</div>}
              <div className="inbox-msg-text">{m.text || <em className="inbox-conv-dim">（无文本 / 附件）</em>}</div>
              <div className="inbox-msg-meta">
                {m.message_type === 'INMAIL' && <span className="inbox-msg-inmail">InMail</span>}
                {fullTime(m.sent_at)}
              </div>
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="inbox-composer">
        {readSyncError && <div className="inbox-read-sync-error" role="alert">{readSyncError}</div>}
        {canReply ? (
          <>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend();
              }}
              placeholder="输入回复…（Ctrl/Cmd + Enter 发送）"
              rows={2}
            />
            <button onClick={handleSend} disabled={sending || !text.trim()}>
              {sending ? '发送中…' : '发送'}
            </button>
          </>
        ) : (
          <div className="inbox-composer-disabled">{replyDisabledReason}</div>
        )}
      </div>
    </div>
  );
}

// ── 主页面 ──
export default function InboxPage({ initialAccountId, onAccountChange }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(initialAccountId || null);
  const [unreadByAccount, setUnreadByAccount] = useState({});

  const [conversations, setConversations] = useState([]);
  const [convCursor, setConvCursor] = useState(null);
  const [convLoading, setConvLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [readSyncError, setReadSyncError] = useState('');

  const selectedAccount = accounts.find((a) => a.account_id === selectedAccountId);
  const accountCanReply = selectedAccount ? isHealthy(selectedAccount.status) : true;
  const canReply = accountCanReply && !selectedConv?.read_only;
  const replyDisabledReason = selectedConv?.read_only
    ? '此对话在 LinkedIn 中为只读，无法回复'
    : '该账号状态异常，暂为只读，请先重连账号';

  // 加载账号列表
  useEffect(() => {
    axios.get(`${API_BASE_URL}/linkedin/accounts`, { timeout: 15000 })
      .then((res) => {
        if (res.data.success) {
          const list = res.data.senders || [];
          setAccounts(list);
          // 默认选中：initialAccountId，否则第一个
          setSelectedAccountId((prev) => prev || (list[0] && list[0].account_id) || null);
        }
      })
      .catch((e) => console.warn('[Inbox] 加载账号失败:', e.message));
  }, []);

  // 顶部 Sender 切换时，收件箱也跟随全局账号切换。
  useEffect(() => {
    if (initialAccountId) setSelectedAccountId(initialAccountId);
  }, [initialAccountId]);

  // 加载未读统计（所有账号，给 tab 徽章）
  const loadUnread = useCallback(() => {
    axios.get(`${API_BASE_URL}/inbox/unread-count`, { timeout: 10000 })
      .then((res) => { if (res.data.success) setUnreadByAccount(res.data.byAccount || {}); })
      .catch(() => {});
  }, []);
  useEffect(() => { loadUnread(); }, [loadUnread]);

  // 加载会话列表
  const loadConversations = useCallback((accountId, useFilter, useQ, cursor) => {
    if (!accountId) return;
    setConvLoading(true);
    const params = { account_id: accountId, filter: useFilter, limit: 30 };
    if (useQ) params.q = useQ;
    if (cursor) params.cursor = cursor;
    axios.get(`${API_BASE_URL}/inbox/conversations`, { params, timeout: 15000 })
      .then((res) => {
        if (res.data.success) {
          setConversations((prev) => (cursor ? [...prev, ...res.data.conversations] : res.data.conversations));
          setConvCursor(res.data.nextCursor);
        }
      })
      .catch((e) => console.warn('[Inbox] 加载会话失败:', e.message))
      .finally(() => setConvLoading(false));
  }, []);

  // 账号 / 过滤 / 搜索 变化 → 重载会话（搜索做个简单防抖）
  useEffect(() => {
    if (!selectedAccountId) return;
    setSelectedConv(null);
    setMessages([]);
    const t = setTimeout(() => {
      loadConversations(selectedAccountId, filter, q, null);
    }, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [selectedAccountId, filter, q, loadConversations]);

  // 加载某会话的消息
  const loadMessages = useCallback((convId) => {
    setMsgLoading(true);
    return axios.get(`${API_BASE_URL}/inbox/conversations/${convId}/messages`, { params: { limit: 50 }, timeout: 15000 })
      .then((res) => { if (res.data.success) setMessages(res.data.messages); })
      .catch((e) => console.warn('[Inbox] 加载消息失败:', e.message))
      .finally(() => setMsgLoading(false));
  }, []);

  // 标记会话已读（本地 + 刷新徽章）；只改受影响的项，避免丢掉“加载更多”的分页结果。
  const applyReadState = useCallback((conversationId, unreadCount = 0) => {
    setConversations((prev) => {
      if (filter === 'unread' && unreadCount === 0) {
        return prev.filter((conversation) => conversation.id !== conversationId);
      }
      return prev.map((conversation) => (conversation.id === conversationId
        ? { ...conversation, unread_count: unreadCount }
        : conversation));
    });
    setSelectedConv((prev) => (prev && prev.id === conversationId
      ? { ...prev, unread_count: unreadCount }
      : prev));
  }, [filter]);

  // 同时清掉 selectedConv 本地未读，避免点击/滚动重复触发。
  const markRead = useCallback((conv) => {
    if (!conv) return Promise.resolve();
    return axios.post(`${API_BASE_URL}/inbox/conversations/${conv.id}/read`, {}, { timeout: 30000 }).then(() => {
      applyReadState(conv.id);
      setReadSyncError('');
      loadUnread();
    }).catch(() => {
      setReadSyncError('已读状态暂未同步到 LinkedIn，请稍后重试');
    });
  }, [applyReadState, loadUnread]);

  // 查看当前对话（点击/滚动右侧）即视为已读。markRead 会把 selectedConv.unread_count 清 0，
  // 所以清完后再触发也会被下面的 guard 挡掉，不会重复发请求。
  const markCurrentRead = useCallback(() => {
    if (selectedConv && selectedConv.unread_count > 0) markRead(selectedConv);
  }, [selectedConv, markRead]);

  // 选择会话 → 加载消息 + 标记已读
  const selectConversation = useCallback((conv) => {
    setReadSyncError('');
    setSelectedConv(conv);
    loadMessages(conv.id);
    if (conv.unread_count > 0) markRead(conv);
  }, [loadMessages, markRead]);

  // 发送回复
  const handleSend = useCallback((text) => {
    if (!selectedConv) return;
    setSending(true);
    axios.post(`${API_BASE_URL}/inbox/conversations/${selectedConv.id}/messages`, { text }, { timeout: 30000 })
      .then((res) => {
        // 乐观追加
        const now = new Date().toISOString();
        setMessages((prev) => [...prev, {
          id: res.data.message_id || `local-${Date.now()}`,
          is_sender: 1, text, message_type: 'MESSAGE', sent_at: now, attachments: [],
        }]);
        setConversations((prev) => prev.map((c) => (c.id === selectedConv.id
          ? { ...c, last_message_preview: text, last_message_at: now, last_message_is_sender: 1, unread_count: 0 } : c)));
        // 回复了 = 已读，顺手清未读
        if (selectedConv.unread_count > 0) markRead(selectedConv);
      })
      .catch((e) => alert('发送失败: ' + (e.response?.data?.error || e.message)))
      .finally(() => setSending(false));
  }, [selectedConv, markRead]);

  // ── SSE 实时推送 ──
  // refreshRef 每次渲染重新赋值，保证 SSE 回调里读到的是最新 state（避免闭包陈旧）
  const refreshRef = useRef(() => {});
  refreshRef.current = (evt) => {
    loadUnread(); // 始终刷新 tab 徽章（任意账号的事件都刷）
    if (evt.account_id !== selectedAccountId) return;

    // read 事件（别处标了已读）：仅把该会话本地未读清掉，不重拉、不再 POST（防循环）
    if (evt.type === 'read') {
      applyReadState(evt.conversation_id);
      return;
    }

    // LinkedIn 网页端的已读事件：以后端从 Unipile 读取到的 unread_count 为准。
    if (evt.type === 'read_state') {
      const unreadCount = Math.max(0, Number(evt.unread_count) || 0);
      applyReadState(evt.conversation_id, unreadCount);
      return;
    }

    // message 事件
    const isOpen = selectedConv && selectedConv.id === evt.conversation_id;
    if (isOpen) loadMessages(selectedConv.id); // 当前打开的对话追加新消息

    if (isOpen && !evt.is_sender) {
      // 正在看这个对话 + 对方来信：先标已读(提交DB)，再刷新列表，
      // 否则全量 loadConversations 会把刚清的未读又读回来（竞态/闪烁）
      markRead(selectedConv)
        .finally(() => { loadConversations(selectedAccountId, filter, q, null); loadUnread(); });
    } else {
      loadConversations(selectedAccountId, filter, q, null); // 列表刷新（新消息置顶）
      loadUnread();
    }
  };

  useEffect(() => {
    const es = new EventSource(`${API_BASE_URL}/inbox/stream`);
    es.onmessage = (e) => {
      try { refreshRef.current(JSON.parse(e.data)); } catch (_) {}
    };
    es.onerror = () => { /* EventSource 会自动重连 */ };
    return () => es.close();
  }, []);

  const selectAccount = useCallback((accountId) => {
    if (!accountId || accountId === selectedAccountId) return;
    setSelectedAccountId(accountId);
    onAccountChange?.(accountId);
  }, [onAccountChange, selectedAccountId]);

  return (
    <div className="inbox-page">
      <AccountTabs
        accounts={accounts}
        selectedId={selectedAccountId}
        unreadByAccount={unreadByAccount}
        onSelect={selectAccount}
      />
      <div className="inbox-main">
        <ConversationList
          conversations={conversations}
          selectedConvId={selectedConv?.id}
          loading={convLoading}
          filter={filter}
          q={q}
          onSelect={selectConversation}
          onFilterChange={setFilter}
          onSearchChange={setQ}
          onLoadMore={() => loadConversations(selectedAccountId, filter, q, convCursor)}
          hasMore={!!convCursor}
        />
        <MessageThread
          conv={selectedConv}
          messages={messages}
          loading={msgLoading}
          canReply={canReply}
          replyDisabledReason={replyDisabledReason}
          readSyncError={readSyncError}
          onSend={handleSend}
          sending={sending}
          onView={markCurrentRead}
        />
      </div>
    </div>
  );
}
